import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../../store/slices/authSlice';
import { useNavigate } from 'react-router-dom';
import { 
  Home, 
  AlertCircle, 
  FileText, 
  Users, 
  CheckSquare, 
  Calendar,
  Star,
  FileEdit,
  Activity,
  BarChart3,
  Settings,
  Menu,
  X,
  LogOut,
  User,
  Wrench,
  Kanban,
  Eye,
  Search,
  UserCog,
  ShieldAlert
} from 'lucide-react';
import GlobalSearch from '../GlobalSearch/GlobalSearch';
import LogoutAnimation from '../Auth/LogoutAnimation';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [showLogoutAnimation, setShowLogoutAnimation] = useState(false);
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, loading } = useSelector((state) => state.auth);
  
  // Auto-expand Tools menu if on a tools page, otherwise allow toggle
  const [toolsMenuOpen, setToolsMenuOpen] = useState(location.pathname.startsWith('/tools'));
  
  // Update menu states when location changes
  useEffect(() => {
    if (location.pathname.startsWith('/tools')) {
      setToolsMenuOpen(true);
    }
    // Close the mobile off-canvas drawer/search after navigating.
    setMobileMenuOpen(false);
    setMobileSearchOpen(false);
  }, [location.pathname]);

  const menuItems = [
    { path: '/dashboard', label: 'Home', icon: Home },
    { path: '/flags', label: 'Flagged Incidents', icon: AlertCircle },
    { path: '/deposit-flag-review', label: 'Flag Review', icon: ShieldAlert },
    { path: '/cases', label: 'Cases', icon: FileText },
    { path: '/watchlist', label: 'Watchlist', icon: Eye },
    { path: '/profiles', label: 'Profiles', icon: Users },
    { path: '/todos', label: 'To-Do\'s', icon: CheckSquare },
    { path: '/calendar', label: 'Calendar', icon: Calendar },
    { path: '/spotlight', label: 'Spotlight', icon: Star },
    { path: '/request-forms', label: 'Request Forms', icon: FileEdit },
    { path: '/activity-log', label: 'Activity Log', icon: Activity },
    { path: '/reports', label: 'Reports', icon: BarChart3 },
    // Users menu item - only visible to Administrators
    ...(user?.role === 'Administrator' ? [{ path: '/users', label: 'Users', icon: UserCog }] : []),
  ];

      const toolsSubmenu = [
        { path: '/tools/task-board', label: 'Task Board', icon: Kanban },
        { path: '/tools/gantt-chart', label: 'Case Timeline', icon: BarChart3 },
      ];

  const handleLogout = () => {
    setShowLogoutAnimation(true);
  };

  const handleLogoutComplete = () => {
    dispatch(logout());
    navigate('/login');
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside
        className={`${
          sidebarOpen ? 'w-64' : 'w-20'
        } bg-white border-r border-gray-200 transition-all duration-300 fixed h-full z-30 ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200">
            {sidebarOpen && (
              <h1 className="text-xl font-bold text-blue-600">CMS</h1>
            )}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-lg hover:bg-gray-100 lg:block hidden"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4">
            <ul className="space-y-1 px-2">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className={`flex items-center px-3 py-2 rounded-lg transition-colors ${
                        active
                          ? 'bg-blue-50 text-blue-600'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <Icon className="w-5 h-5 flex-shrink-0" />
                      {sidebarOpen && (
                        <span className="ml-3 font-medium">{item.label}</span>
                      )}
                    </Link>
                  </li>
                );
              })}

              {/* Tools Menu */}
              <li>
                <button
                  onClick={() => setToolsMenuOpen(!toolsMenuOpen)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                    location.pathname.startsWith('/tools')
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center">
                    <Wrench className="w-5 h-5 flex-shrink-0" />
                    {sidebarOpen && (
                      <span className="ml-3 font-medium">Tools</span>
                    )}
                  </div>
                  {sidebarOpen && (
                    <span className={`transform transition-transform ${toolsMenuOpen ? 'rotate-90' : ''}`}>
                      ›
                    </span>
                  )}
                </button>
                {sidebarOpen && toolsMenuOpen && (
                  <ul className="ml-8 mt-1 space-y-1">
                    {toolsSubmenu.map((item) => {
                      const Icon = item.icon;
                      const active = isActive(item.path);
                      return (
                        <li key={item.path}>
                          <Link
                            to={item.path}
                            className={`flex items-center px-3 py-2 rounded-lg transition-colors text-sm ${
                              active
                                ? 'bg-blue-50 text-blue-600 font-medium'
                                : 'text-gray-600 hover:bg-gray-50'
                            }`}
                          >
                            <Icon className="w-4 h-4 flex-shrink-0" />
                            <span className="ml-3">{item.label}</span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            </ul>
          </nav>

          {/* User section */}
          <div className="border-t border-gray-200 p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <User className="w-5 h-5 text-blue-600" />
              </div>
              {sidebarOpen && (
                <div className="flex-1 min-w-0">
                  {loading || !user ? (
                    <div className="space-y-1">
                      <div className="h-4 bg-gray-200 rounded animate-pulse w-24"></div>
                      <div className="h-3 bg-gray-200 rounded animate-pulse w-16"></div>
                    </div>
                  ) : (
                    <>
                      <Link
                        to="/profile"
                        className="block hover:opacity-80 transition-opacity"
                      >
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {user?.firstName} {user?.lastName}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{user?.role}</p>
                      </Link>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className={`flex-1 flex flex-col min-w-0 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'} transition-all duration-300`}>
        {/* Header */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 lg:px-6">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 rounded-lg hover:bg-gray-100 lg:hidden"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>

          {/* Global Search */}
          <div className="hidden md:block flex-1">
            <GlobalSearch />
          </div>

          <div className="flex items-center space-x-4">
            {/* Mobile Search Button */}
            <button
              onClick={() => setMobileSearchOpen(!mobileSearchOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100"
            >
              <Search className="w-5 h-5 text-gray-600" />
            </button>
            <Link
              to="/profile"
              className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <User className="w-4 h-4" />
              <span className="hidden sm:inline">Profile</span>
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </header>

        {/* Mobile Search */}
        {mobileSearchOpen && (
          <div className="md:hidden border-b border-gray-200 bg-white p-4">
            <GlobalSearch />
          </div>
        )}

        {/* Page content */}
        <main className="flex-1 min-w-0 overflow-y-auto p-4 lg:p-6">
          {children}
        </main>
      </div>

      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Logout Animation */}
      {showLogoutAnimation && (
        <LogoutAnimation onComplete={handleLogoutComplete} />
      )}
    </div>
  );
};

export default Layout;

